import { Fragment, useRef, useState, useEffect, useMemo } from "react";

import { useNode } from "@craftjs/core"
import { get, omit } from "lodash";
import { MessageCircleQuestion, Monitor, Smartphone, Link } from "lucide-react";
import { AccordionList, AccordionListContent, AccordionListItem, AccordionListTrigger, DefaultTooltip, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, List, ListItem } from "twyne-new.ui";

import { useAvailableProps, useResponsive, usePropsIgnoreDevice, useFilteredProps, useUtilities } from "@/modules/cms/views/post/components/builder/context";
import Tabs from '@Components/Tabs';

import { useCraftCustom } from "../core/hooks";

import ClearProp from "./ClearProp";
import Droppable from "./Droppable";
import { StateProvider, StateToggleComponent, StateRegisterWrapper } from "./State";
import ToolbarSidebar from "./Toolbar";
import UtilitiesBreadcrumb from "./Utilities/Breadcrumb";
import UtilitiesSidebarAnimation from "./Utilities/SidebarAnimation";
import { DefinitionProvider, useDefinitionContext } from "./context";
import { BlockDefinition } from "./definition";
import { useCustomProp, isInheriting, isDeviceUtilityStructure, propHasAnyValue, usePureCraftProp } from "./hooks";
import { PROPERTIES } from "./properties";
import { useStylePatterns } from "./tailwindConfig/hooks";

export * from './hooks'
export * from './properties'

export const SHARED_KEYS = [
  'form-container',
  'form-step',
  'form-row',
  'form-col',
  'form-label',
  'form-input',
  'form-textarea',
  'form-select',
  'form-checkbox',
  'form-date',
  'form-dob',
  'form-feedback-message',
  'form-continue-button',
  'form-previous-button',
  'form-button-grid',
  'form-buttons-grid-container',
  'form-checkbox-multi',
  'form-checkbox-multi-container',
  'form-checkbox-multi-input',
  'form-checkbox-multi-label',
  'form-radio',
  'form-radio-container',
  'form-radio-input',
  'form-radio-label',
  'form-radio-multi',
  'form-radio-multi-container',
  'form-radio-multi-input',
  'form-radio-multi-label',
  'form-consent',
  'form-consent-input',
  'form-consent-label',
  'form-consent-instructions-container',
  'form-consent-instructions-text',
  'form-heading-h1',
  'form-heading-h2',
  'form-heading-h3',
  'form-heading-h4',
  'form-heading-h5',
  'form-heading-h6',
  'form-date-triple-select',
  // TCPA blocks
  'tcpa-questionary',
  'tcpa-question',
  'tcpa-answers',
  'tcpa-item-answer',
  'tcpa-answer',
  'tcpa-item-answer-checkbox',
  'tcpa-consent-text',
  'tcpa-continue-button',
  'tcpa-continue-container',
  'tcpa-required-feedback',
]

const DeviceToggle = ({ open }) => {
  const { device, handleDevice } = useResponsive()

  if (!open && device === 'desktop') {
    return null
  }

  const handleChange = (device, size) => {
    handleDevice(device, size)
  }

  return <>
    <DropdownMenu size='sm'>
      <DropdownMenuTrigger asChild>
        {device === 'desktop' ? <Monitor size='12' /> : <Smartphone size='12' />}
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem icon={Monitor} iconSize={12} onClick={() => handleChange('desktop')} />
        <DropdownMenuItem icon={Smartphone} iconSize={12} onClick={() => handleChange('mobile')} />
      </DropdownMenuContent>
    </DropdownMenu>
  </>
}

function getAllKeys({ rootKeys = [], props }) {
  if (rootKeys.length > 1) {
    return rootKeys
  }

  const allKeys = []

  for (const key of rootKeys) {
    const value = get(props, key) || {}
    if (typeof value === 'object' && !isDeviceUtilityStructure(value)) {
      allKeys.push(Object.keys(value).map(item => `${key}.${item}`))
    }
  }

  if (allKeys.flat().length) {
    return allKeys.flat()
  }

  const parseAttributesKeys = rootKeys.map(key => key.replace('[', '.').replace(']', ''))
  return parseAttributesKeys
}

function ShouldRenderWrapper({ useShouldRender = useDefaultUseShouldRender, children, keys, name, blockSidebarProps }) {
  const { value: filteredProps } = useFilteredProps()
  const { value: suppressSidebarProps = [] } = useCustomProp('suppressSidebarProps')
  const shouldRender = useShouldRender()
  const parseByFilter = filteredProps ? filteredProps.some(filterKey => keys.some(key => key.startsWith(filterKey))) : true
  const parseByBlock = blockSidebarProps.includes(name)
  const parseBySuppress = suppressSidebarProps.includes(name)
  const render = shouldRender && parseByFilter && parseByBlock && !parseBySuppress

  return render ? children : null
}

function useDefaultUseShouldRender() {
  return true
}

function PropertyField({ name, label, type, component: Component = PROPERTIES[type], fixedLabel, hint, blockClear, keys, properties, ...props }) {
  const { value: propsIgnoreDevice } = usePropsIgnoreDevice()
  const { device } = useResponsive()
  const { utilitiesParsed } = useUtilities()
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)
  const { value: stylePattern } = usePureCraftProp('properties.stylePattern')
  const { value: patternData } = useStylePatterns(stylePattern)

  const handleMouseLeave = e => {
    try {
      if (containerRef.current && containerRef.current?.contains(e.relatedTarget)) {
        return
      }

      setOpen(false)
    } catch (error) {
      return
    }
  }

  const ignoreByDevice = useMemo(() => propsIgnoreDevice?.includes(name), [name, propsIgnoreDevice])
  const { inheriting, deviceHasValue, inheritingBothDevices, inheritingFromPattern } = useMemo(() => isInheriting({ props: properties, keys, device, utilitiesParsed, type, patternData, stylePattern }), [properties, keys, device, utilitiesParsed, type, patternData, stylePattern])
  const hasAnyValue = useMemo(() => propHasAnyValue(properties, keys), [properties, keys])

  const fixedLabelClasses = fixedLabel ? 'flex flex-col gap-2' : 'grid grid-cols-3 gap-1'
  const inheritingClasses = inheriting && !ignoreByDevice || inheritingFromPattern ? 'opacity-50 hover:!bg-transparent' : ''
  const labelClasses = `self-${fixedLabel ? 'start' : 'center'}`
  const contentClasses = `col-span-${label ? 2 : 3}`
  const containerClasses = `prop-item ${hasAnyValue ? `has-value-${name?.replaceAll('.', '-')}` : ''}`

  const ValueComponent = <>
    <p className={`${labelClasses} justify-self-start col-span-1 flex gap-1 items-center`}>
      <p>{label}</p>
      {hint && <DefaultTooltip side='right' contentProps={{ className: 'max-w-40' }} content={hint}> <MessageCircleQuestion size='12' /> </DefaultTooltip>}
      {label && !ignoreByDevice ? <DeviceToggle open={open} /> : null}
      {!blockClear && !inheriting && deviceHasValue && open ? <ClearProp keys={keys} /> : null}
      {inheritingBothDevices.length && !ignoreByDevice && open ? <DefaultTooltip side='right' content={<div> Inherited from another device: {inheritingBothDevices.map(item => <div key={item}>{item}</div>)} </div>}> <Link size='12' /> </DefaultTooltip> : null}
      {inheritingFromPattern ? <DefaultTooltip side='right' content={<div> Inherited from pattern: {inheritingFromPattern} </div>}> <Link size='12' /> </DefaultTooltip> : null}
    </p>
    <div className={contentClasses}>
      <Component {...props}
        type={type}
        name={name}
      />
    </div>
  </>

  return <div ref={containerRef} onMouseEnter={() => label && setOpen(true)} onMouseLeave={handleMouseLeave} className={containerClasses}>
    <ListItem className={`${fixedLabelClasses} ${inheritingClasses}`} value={ValueComponent} />
  </div>
}

function mountBlock(definition) {
  let { key, name, Component, craft, icon, Template, wrappers, category, variants, BlockProvider } = definition

  const DefinitionWrapper = ({ children }) => <BlockProvider>
    <BlockDefinition definition={definition}>
      {children}
    </BlockDefinition>
  </BlockProvider>

  Component = [DefinitionWrapper, ...wrappers].reduce((Component, Wrapper) => props => <Wrapper>
    <Component {...props} />
  </Wrapper>, Component)

  Component.craft = craft

  const palette = !icon ? null : {
    text: name,
    element: <Component />,
    icon: icon,
    category,
    variants,
  }

  return {
    key,
    Component,
    Template,
    palette,
  }
}

export function Tab({ tab, children }) {
  return <Tabs.Tab tab={tab}>
    <div style={{ height: '76vh', overflowY: 'scroll', marginBottom: '-25px' }}>
      {children}
    </div>
  </Tabs.Tab>
}

export function define(key, Component, Tag = 'div') {
  const contextMapping = {}
  const definition = {
    key, Component, wrappers: [], Context: Fragment, related: {},
    context: {
      useValue: () => {
        const { id } = useNode()
        return contextMapping[id] || {}
      },
      useUpdate: () => {
        const context = useDefinitionContext()
        const { id } = useNode()
        contextMapping[id] = context
      },
      useDelete: () => {
        const { id } = useNode()
        useEffect(() => () => {
          delete contextMapping[id]
        }, [id])
      },
    },
  }

  const BlockProvider = definition.BlockProvider = ({ children }) => <DefinitionProvider definition={definition}>
    {children}
  </DefinitionProvider>

  /**
   * @type {import('./properties').BaseBuilder[]}
   */
  const props = new class extends Array {
    mount() {
      const properties = props.map(prop => {
        prop = prop.build()
        if (prop.ignoreDevice) {
          definition.ignoreDeviceProps = [...definition.ignoreDeviceProps || [], prop.name]
        }
        return prop
      })

      const { Context } = definition

      const Properties = () => <Context>
        <RightSidebarProps sidebarProps={properties} definition={definition} />
      </Context>

      return Properties
    }

    build() {
      return props.map(prop => prop?.build ? prop.build() : {})
    }
  }

  return new class {
    validIgnoreDevice() {
      if (definition?.ignoreDeviceProps?.length) {
        this.wrap(({ children }) => {
          const { value, setValue } = usePropsIgnoreDevice()
          const newIgnore = definition.ignoreDeviceProps.filter(prop => !value?.includes(prop))
          newIgnore.length && setValue([...value, ...newIgnore])
          return children
        })
      }
    }

    wrap(wrapper) {
      definition.wrappers.push(wrapper)
      return this;
    }

    shared(property, opts) {
      this.property(property)
      this.wrap(({ children }) => {
        const { props } = useNode(({ data: { props } }) => ({ props: props?.properties }))
        usePureCraftProp('properties.stylePattern', property._type)

        const { value: patternData, setValueIgnoring: setPattern } = useStylePatterns(property._type)

        const hasInitializedRef = useRef(false)

        useEffect(() => {
          if (hasInitializedRef.current) return
          if (Object.keys(props || {}).length <= 1) return
          if (JSON.stringify(patternData) !== '{}') return

          hasInitializedRef.current = true

          setPattern({ ...omit(props, ['attributes', 'stylePattern']), opts })
        }, [props, patternData, setPattern])

        return children
      })

      definition.isShared = true;

      return this;
    }

    droppable() {
      definition.wrappers.push(Droppable)
      return this;
    }

    context(Context) {
      definition.Context = Context
      return this;
    }

    canvas(value = true) {
      definition.isCanvas = value
      return this;
    }

    hideProps(hideProps = []) {
      definition.hideProps = hideProps
      return this;
    }

    palete(icon, name, category = 'default') {
      definition.icon = icon;
      definition.name = name;
      definition.category = category;
      definition.canDuplicate = true;
      definition.canSave = true;
      return this;
    }

    canDuplicate(value = true) {
      definition.canDuplicate = value;
      return this;
    }

    canSave(value = true) {
      definition.canSave = value;
      return this;
    }

    rules(value) {
      definition.rules = value;
      return this;
    }

    tag({ attributes: attributesProp = 'attributes', children: childrenProp = 'children', propTagName } = {}) {
      if (typeof Tag === 'function') {
        definition.Template = Tag
      } else {
        definition.Template = ({ [childrenProp]: children, [attributesProp]: attributes, [propTagName]: tagName, ...props }) => {
          Tag = tagName || Tag
          if (attributesProp === 'props') {
            attributes = props
          }
          if (!childrenProp) {
            return <Tag {...attributes} />
          }
          return <Tag {...attributes}>{children}</Tag>
        }
      }
      return this;
    }

    defaultProps(properties) {
      definition.defaultProps = { properties };
      return this;
    }

    property(...properties) {
      props.push(...properties)
      return this
    }

    showOnAddProps(...properties) {
      definition.showOnAddProps = properties
      return this
    }

    ignoreResetProps(properties) {
      definition.ignoreResetProps = properties
      return this
    }

    variant({ prop, variants }) {
      prop && this.property(prop);
      definition.variants = variants;
      return this;
    }

    related(related) {
      definition.related = {
        ...definition.related,
        ...related,
      }
      return this
    }

    state() {
      this.related({ StateComponent: StateToggleComponent })

      return this.wrap(({ children }) => {
        const { value: state } = useCraftCustom('state')

        if (!state) {
          return children
        }

        const { label, prop = 'show' } = state

        return <StateProvider label={label} prop={prop}>
          <StateRegisterWrapper>
            {children}
          </StateRegisterWrapper>
        </StateProvider>
      })
    }

    registerState(label, prop) {
      return this
        .state()
        .custom({ state: { label, prop } })
    }

    name(name) {
      definition.name = name
      return this
    }

    custom(custom) {
      definition.custom = {
        ...definition.custom,
        ...custom,
      }
      return this
    }

    build() {
      const Properties = props.mount()
      const Props = props.build()
      const { Context, rules, defaultProps, isCanvas, canDuplicate, related, name = key, custom } = definition

      this.validIgnoreDevice()

      definition.craft = {
        related: {
          Properties,
          Context,
          BlockProvider,
          ...related,
        },
        rules,
        props: defaultProps,
        custom: {
          name,
          canDuplicate,
          ...custom,
        },
        isCanvas,
      }

      definition.props = Props

      if (!definition.Template) {
        this.tag()
      }

      return mountBlock(definition);
    }
  }
}

const handleMissingProps = ({ sidebarProps, blockSidebarProps, onChangeBlockProps }) => {
  const missingProps = sidebarProps.map(item => item.name).filter(prop => !blockSidebarProps.includes(prop))
  missingProps.length && onChangeBlockProps([...blockSidebarProps, ...missingProps])
}

const RightSidebarProps = ({ sidebarProps, definition: { defaultProps, hideProps = [], showOnAddProps, ignoreResetProps = [] } }) => {
  const { properties: craftProps } = useNode(({ data: { props: { properties } } }) => ({ properties }))

  const availableProps = useAvailableProps(showOnAddProps)
  const { value: blockSidebarProps = [], setValue: setBlockSidebarProps } = useCustomProp('sidebarProps')
  const allKeys = []
  const allProps = useMemo(() => mergeBlockWithAvailableProps({ sidebarProps, availableProps }), [sidebarProps, availableProps])

  useEffect(() => {
    handleMissingProps({ sidebarProps, blockSidebarProps, onChangeBlockProps: setBlockSidebarProps })
  }, [sidebarProps, blockSidebarProps, setBlockSidebarProps])

  if (!blockSidebarProps.length) {
    return null
  }

  return <>
    <ToolbarSidebar properties={{ hideProps, showOnAddProps }} resetProps={{ defaultProps, allKeys, ignoreResetProps }} />
    <UtilitiesBreadcrumb />
    <AccordionList type='multiple' defaultValue={[...Object.keys(availableProps), ...allProps.map(([key]) => key)]}>
      <UtilitiesSidebarAnimation>
        {allProps?.map(([key, itens]) => itens && itens.length && <div key={key} className='has-[ul:empty]:hidden'>
          <CategoryItem key={key} name={key} itens={itens} craftProps={craftProps} blockSidebarProps={blockSidebarProps} allKeys={allKeys} />
        </div>)}
      </UtilitiesSidebarAnimation >
    </AccordionList>
  </>
}

const CategoryItem = ({ name, itens, craftProps, blockSidebarProps, allKeys }) => <AccordionListItem value={name}>
  <AccordionListTrigger className='capitalize'>{CATEGORIES_NAME?.[name] || name}</AccordionListTrigger>
  <AccordionListContent>
    <List>
      {itens.map(({ useShouldRender, name, keys: rootKeys = [name], ...props }, idx) => {
        const keys = getAllKeys({ rootKeys, props: craftProps })
        allKeys.push(...keys)

        return <ShouldRenderWrapper
          key={idx}
          keys={keys}
          name={name}
          useShouldRender={useShouldRender}
          blockSidebarProps={blockSidebarProps}
        >
          <PropertyField {...props} name={name} keys={keys} properties={craftProps} />
        </ShouldRenderWrapper>
      })}
    </List>
  </AccordionListContent>
</AccordionListItem>

function mergeBlockWithAvailableProps({ sidebarProps, availableProps }) {
  const result = { ...availableProps }

  for (const prop of sidebarProps) {
    const category = prop.category?.toLowerCase() || 'none'
    if (!result[category]) {
      result[category] = []
    }

    const existingIndex = result[category].findIndex(p => p.name === prop.name)
    if (existingIndex === -1) {
      result[category].push(prop)
    } else {
      result[category][existingIndex] = prop
    }
  }

  const orderedCategories = CATEGORIES_SEQUENCE.map(category => [category, result[category]])
  return orderedCategories
}

const CATEGORIES_NAME = {
  default: 'Default',
  standard: 'Standard',
  appearance: 'Appearance',
  typography: 'Typography',
  borders: 'Borders',
  flexGrid: 'Flex & Grid',
  effect: 'Effect',
  layout: 'Layout',
  interactivity: 'Interactivity',
  svg: 'SVG',
}

const CATEGORIES_SEQUENCE = [
  'default',
  'standard',
  'appearance',
  'typography',
  'borders',
  'flexGrid',
  'effect',
  'layout',
  'interactivity',
  'svg',
]
